import { useSignal } from "@preact/signals";
import { openSettings } from "../settings/SettingsSheet.js";
import { useEffect } from "preact/hooks";
import games from "virtual:obean-games";
import { greeting } from "../greeting.js";
import type { Guest, Hosting } from "../net/api.js";
import { ApiError, NICKNAME_ERRORS, ON_HOLD, api } from "../net/api.js";
import { navigate } from "../router.js";
import { HostingLimit } from "./PassCards.js";
import { WelcomeBack } from "../home/WelcomeBack.js";
import { forgetInvites, recallLeft, rememberInvite, rememberRoomGame } from "./invite-memory.js";

/** Start here: your name, then a room to invite a friend to (P2-WEB-01). */
export function Landing() {
  // From a game's "Play with friends" on the website (`/new?game=<id>`): the room starts with it.
  const game = games.find((g) => g.id === new URLSearchParams(location.search).get("game"));
  const guest = useSignal<Guest | null>(null);
  const name = useSignal("");
  const error = useSignal<string | null>(null);
  const busy = useSignal(false);
  const deleted = useSignal(false);
  /** Today's free rooms are used up (ADR 0022): said before a room is made that won't open. */
  const full = useSignal<Hosting | null>(null);

  useEffect(() => {
    api
      .guest()
      .then((g) => {
        guest.value = g;
        // Someone who started typing before this arrived keeps what they typed.
        if (name.value === "") name.value = g.nickname ?? "";
      })
      .catch(() => {
        error.value = "Can't reach Obean right now. Try again in a moment.";
      });
  }, []);

  const create = async (e: Event) => {
    e.preventDefault();
    busy.value = true;
    error.value = null;
    try {
      if (name.value.trim() !== (guest.value?.nickname ?? "")) await api.setNickname(name.value);
      // A failed check isn't a refusal: the room page says so too, if it comes to that.
      const hosting = await api.me().then(
        (me) => me.hosting,
        () => null,
      );
      if (hosting && hosting.nextFreeAt !== null) {
        full.value = hosting;
        return;
      }
      const { roomId, invite } = await api.createRoom();
      rememberInvite(roomId, invite);
      if (game) rememberRoomGame(roomId, game.id);
      navigate(`/r/${roomId}`);
    } catch (err) {
      error.value =
        err instanceof ApiError
          ? err.code === "on_hold"
            ? ON_HOLD
            : (NICKNAME_ERRORS[err.code] ?? "Something went wrong. Try again.")
          : "Can't reach Obean right now. Try again in a moment.";
    } finally {
      busy.value = false;
    }
  };

  if (deleted.value)
    return (
      <section class="landing">
        <h1>{greeting(import.meta.env.MODE)}</h1>
        <div class="card" role="status">
          <h2>Your data is deleted</h2>
          <p>Your name is gone, and nothing links this browser to your past rooms and games.</p>
          <button
            type="button"
            onClick={() => {
              location.reload();
            }}
          >
            Start again
          </button>
        </div>
      </section>
    );

  // The room this tab just left (#616): the way back, while it may still be open.
  const left = recallLeft();
  return (
    <section class="landing">
      <h1>{greeting(import.meta.env.MODE)}</h1>
      <p>
        {game
          ? `Make a room for ${game.displayName}, send your friends the link, and play together on any laptop or phone. No app, no account.`
          : "Make a room, send your friends the link, and play together on any laptop or phone. No app, no account."}
      </p>
      {/* Someone who has played here before: back in play in one tap (P5-UX-04). */}
      {!game && <WelcomeBack />}
      <form
        class="card"
        onSubmit={(e) => {
          void create(e);
        }}
      >
        <label for="name">Your name</label>
        <input
          id="name"
          value={name.value}
          onInput={(e) => {
            name.value = e.currentTarget.value;
          }}
          maxLength={20}
          autocomplete="nickname"
          required
        />
        {error.value && (
          <p role="alert" class="error">
            {error.value}
          </p>
        )}
        <button type="submit" disabled={busy.value || guest.value === null}>
          Create a room
        </button>
      </form>
      {full.value && <HostingLimit hosting={full.value} roomId={null} back="/new" />}
      {left && (
        <p class="solo-link">
          Left by mistake? <a href={`/r/${left}`}>Back to the room you left</a>.
        </p>
      )}
      <p class="solo-link">
        Nobody to play with right now?{" "}
        {game ? (
          <>
            <a href={`/solo?game=${game.id}`}>Play {game.displayName} solo</a>.
          </>
        ) : (
          <>
            <a href="/solo">Play solo</a>: pick any game, with bots.
          </>
        )}
      </p>
      {guest.value && (
        <YourData
          onDeleted={() => {
            forgetInvites();
            guest.value = null;
            name.value = "";
            deleted.value = true;
          }}
        />
      )}
      <p class="hint">
        <button type="button" class="link" onClick={openSettings}>
          Settings
        </button>
        : sound, buzzing, less motion, larger text.
      </p>
      {/* The website's pages (static, ADR 0015): site/build.tsx renders them. */}
      <nav class="site-links" aria-label="About Obean">
        <a href="/">Home</a>
        <a href="/games">Games</a>
        <a href="/help">Help</a>
        <a href="/safety">Safety</a>
        <a href="/status">Status</a>
        <a href="/about">About</a>
        <a href="/contact">Contact</a>
      </nav>
    </section>
  );
}

/**
 * Delete my data (P2-SAFE-02, docs/safety-and-privacy.md): asks first. The api removes the guest
 * and its name, and puts a nameless tombstone in its place in results, events and reports.
 */
function YourData(props: { onDeleted: () => void }) {
  const confirming = useSignal(false);
  const busy = useSignal(false);
  const failed = useSignal(false);
  const remove = async () => {
    busy.value = true;
    failed.value = false;
    try {
      await api.deleteMe();
      props.onDeleted();
    } catch {
      failed.value = true;
    } finally {
      busy.value = false;
    }
  };
  if (!confirming.value)
    return (
      <div class="your-data">
        <button
          type="button"
          class="link"
          onClick={() => {
            confirming.value = true;
          }}
        >
          Delete my data…
        </button>
      </div>
    );
  return (
    <div class="your-data" role="group" aria-label="Delete my data">
      <p>
        Delete your name and this browser's link to your rooms and games? Game results stay, without
        your name. This can't be undone.
      </p>
      <div class="actions">
        <button
          type="button"
          class="danger"
          disabled={busy.value}
          onClick={() => {
            void remove();
          }}
        >
          Delete my data
        </button>
        <button
          type="button"
          class="secondary"
          onClick={() => {
            confirming.value = false;
            failed.value = false;
          }}
        >
          Cancel
        </button>
      </div>
      {failed.value && <p role="alert">That didn't go through. Try again in a moment.</p>}
    </div>
  );
}
